import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { ActiveModal, HcPopComponent } from "@healthcatalyst/cashmere";
import { Subject, takeUntil } from "rxjs";
import { TypeElement, TypeService } from "src/app/shared/services/type.service";

/** Admin string editor to be used on types, statuses, product lines, and SLAs */
@Component({
    selector: 'pd-admin-string-editor',
    templateUrl: './admin-string-editor.component.html',
    styleUrls: ['./admin-string-editor.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdminStringEditorComponent implements OnInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    loading = true;
    loadError = false;
    type: string;
    category: string;
    description: string;
    stringList: any[] = [];
    element: TypeElement;
    nameControl = new FormControl('', { nonNullable: true, validators: [Validators.required] });
    descriptionControl = new FormControl('', { nonNullable: true, validators: [Validators.required] });

    quillConfig = {
        toolbar: [['bold', 'italic', 'underline', 'strike'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean']]
    };
    quillStyles = {
        'font-family': "'Noto Sans', 'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif"
    };

    @ViewChild('removePop')
    removePop: HcPopComponent;

    @ViewChild('addEditPop')
    addEditPop: HcPopComponent;

    constructor(
        private activeModal: ActiveModal,
        private typeService: TypeService
    ) {
        this.type = this.activeModal.data.type;
        this.category = this.activeModal.data.category;
    }

    get countText(): string {
        let countText = '';

        if (this.stringList.length === 0) {
            countText = `0 ${this.category} ${this.type}s`
        } else if (this.stringList.length > 1) {
            countText = `${this.stringList.length} ${this.category} ${this.type}s`
        } else {
            countText = `${this.stringList.length} ${this.category} ${this.type}`
        }

        return countText;
    }

    ngOnInit() {
        this.typeService.catTypeList.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list && this.typeService.category === this.category.toLowerCase()) {
                this.stringList = list;
                this.stringList.sort((a, b) =>
                    a.name.localeCompare(b.name)
                )
                this.loading = false;
            } else {
                this.loadError = this.typeService.loadFailed;
                !this.loadError ? this.loadData() : this.loading = false;
            }
        })
    }

    loadData(): void {
        this.typeService.loadCatTypes(this.category.toLowerCase());
        this.typeService.loadAllTypes();
    }

    setStringField(): void {
        this.nameControl.reset();
        this.nameControl.setValue(this.element.name);
        this.descriptionControl.setValue(this.element.description);
    }

    resetAddEditPop(): void {
        this.nameControl.reset();
        this.descriptionControl.reset();
        this.addEditPop.close();
    }

    remove(el: any) {
        this.typeService.removeType(el).pipe(takeUntil(this.unsubscribe)).subscribe(() => {
            this.loadData();
            this.removePop.close();
        });
    }

    save(): void {
        if (this.element) {
            this.typeService.updateType({
                typeID: this.element.typeID,
                name: this.nameControl.value,
                category: this.element.category,
                description: this.descriptionControl.value
            }).pipe(takeUntil(this.unsubscribe)).subscribe(() => {
                this.loadData();
            })
        } else {
            this.typeService.addType({
                typeID: -1,
                name: this.nameControl.value,
                category: this.category.toLowerCase(),
                description: this.descriptionControl.value
            }).pipe(takeUntil(this.unsubscribe)).subscribe(() => {
                this.loadData();
            })
        }
        this.resetAddEditPop();
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}